// Trend calculation utilities for parameter cards
// Compares recent measurements to determine if values are trending up, down, or flat.

import { CHART_PARAMETERS } from './chartUtils'

/**
 * Trend direction constants
 */
export const TREND_DIRECTION = {
  UP: 'up',
  DOWN: 'down',
  FLAT: 'flat',
  UNKNOWN: 'unknown'
}

/**
 * Thresholds for determining if a change is significant enough to show a trend arrow.
 * Values below the threshold are considered "flat".
 * Each key matches a measurement field name.
 */
export const TREND_THRESHOLDS = {
  free_chlorine: 0.5,     // ppm
  combined_chlorine: 0.2, // ppm
  ph: 0.1,                // pH units
  total_alkalinity: 5,    // ppm
  cya_stabilizer: 3,      // ppm
  calcium_hardness: 10,   // ppm
  water_temperature: 2    // degrees F
}

/**
 * Calculate the trend direction for a single parameter given two measurement values.
 * Compares the latest value to the previous value using parameter-specific thresholds.
 *
 * @param {string} parameterKey - The measurement field name (e.g. 'free_chlorine')
 * @param {number|null|undefined} latestValue - The most recent measurement value
 * @param {number|null|undefined} previousValue - The prior measurement value
 * @returns {string} One of TREND_DIRECTION values
 */
export const calculateTrend = (parameterKey, latestValue, previousValue) => {
  // If either value is missing, we cannot determine a trend
  if (latestValue === null || latestValue === undefined ||
      previousValue === null || previousValue === undefined) {
    return TREND_DIRECTION.UNKNOWN
  }

  const threshold = TREND_THRESHOLDS[parameterKey]
  if (threshold === undefined) {
    return TREND_DIRECTION.UNKNOWN
  }

  const difference = latestValue - previousValue

  if (difference > threshold) {
    return TREND_DIRECTION.UP
  } else if (difference < -threshold) {
    return TREND_DIRECTION.DOWN
  } else {
    return TREND_DIRECTION.FLAT
  }
}

/**
 * The five key parameters to display on the Dashboard at-a-glance cards.
 */
export const DASHBOARD_PARAMETERS = ['free_chlorine', 'ph', 'total_alkalinity', 'cya_stabilizer', 'calcium_hardness']

/**
 * Build an array of parameter summary objects from the two most recent measurements.
 * Each summary includes the latest value, its unit/label info, and the trend direction.
 *
 * @param {Object|null} latestMeasurement - The most recent measurement row
 * @param {Object|null} previousMeasurement - The second most recent measurement row
 * @returns {Array<Object>} Array of { key, label, shortLabel, unit, color, value, trend }
 */
export const buildParameterSummaries = (latestMeasurement, previousMeasurement) => {
  return DASHBOARD_PARAMETERS.map(key => {
    const chartParam = CHART_PARAMETERS[key]
    const latestValue = latestMeasurement ? latestMeasurement[key] : null
    const previousValue = previousMeasurement ? previousMeasurement[key] : null

    return {
      key,
      label: chartParam?.label || key,
      shortLabel: chartParam?.shortLabel || key,
      unit: chartParam?.unit || '',
      color: chartParam?.color || '#999',
      value: latestValue ?? null,
      trend: calculateTrend(key, latestValue, previousValue)
    }
  })
}

/**
 * Format a parameter value for display on a card.
 * Returns '--' for null/undefined values.
 *
 * @param {number|null|undefined} value - The parameter value
 * @param {string} parameterKey - The measurement field name
 * @returns {string} Formatted display string
 */
export const formatParameterValue = (value, parameterKey) => {
  if (value === null || value === undefined) {
    return '--'
  }

  if (parameterKey === 'ph') {
    return value.toFixed(1)
  }

  // For integer-range parameters, show no decimals if the value is a whole number
  if (parameterKey === 'total_alkalinity' || parameterKey === 'cya_stabilizer' || parameterKey === 'calcium_hardness') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(1)
  }

  // Chlorine values: always one decimal
  return value.toFixed(1)
}
